import { __assign, __extends } from "tslib";
import { toolbox } from '../../externals/bss-package-toolbox/js/toolbox.js';
import Formatter from '../../externals/bss-package-formatter/js/formatter.js';
import logger from '@main/externals/bss-package-logger/js/logger';
import EntityBuilder from '../bss-entity-builder/js/bss-entity-builder.js';
import FontFaceObserver from 'fontfaceobserver';
import { parseRequest } from './js/parse-request';
import simulatorErrorTemplate from '../../view/components/bss-error-page-component/template/errorPage.hbs';
import simulatorRevertTemplate from '../../view/components/bss-entity-revert-info-component/template/revertInfo.hbs';
import { BaseControllerToolbox } from '@main/core/bss-connector/base-controller-toolbox';
var HIDDEN_CLASS = 'bss-entity--is-hidden';
var UC_PUBLISHER_NAME = 'bss-connector';
var UC_SIMULATOR_ID = '{usedCarsSimulatorId}';
var simulatorTemplates = {
    errorTemplate: simulatorErrorTemplate,
    revertTemplate: simulatorRevertTemplate,
};
/**
 * @desc BSSControllerToolbox
 */
var BSSControllerToolbox = /** @class */ (function (_super) {
    __extends(BSSControllerToolbox, _super);
    function BSSControllerToolbox(environment, resolutionsConfig, version, components, descriptionConfig, flowProps, setFlowConfigurationCallback) {
        if (environment === void 0) { environment = null; }
        if (resolutionsConfig === void 0) { resolutionsConfig = null; }
        if (version === void 0) { version = 'prod'; }
        if (components === void 0) { components = null; }
        if (descriptionConfig === void 0) { descriptionConfig = null; }
        if (flowProps === void 0) { flowProps = null; }
        if (setFlowConfigurationCallback === void 0) { setFlowConfigurationCallback = null; }
        var _this = _super.call(this, environment, resolutionsConfig, version, components, descriptionConfig, flowProps, setFlowConfigurationCallback) || this;
        /**
         * @desc make feature specific simulator request to server,
         *  to return 'desired' data from server to be for custom save event on generic close button on Italy market
         * @return promise, on resolve it returns calculated offer
         * @private
         */
        _this._makeDesiredDataRequest = function (publishername, params) {
            var url = params.url, error = params.error, success = params.success;
            _this._showOverlay();
            var requestParams = params === null || params === void 0 ? void 0 : params.params;
            if (!params) {
                var requestData = _this._getOfferDataForLeadGenerationCall();
                url = requestData.url;
                requestParams = requestData.params;
            }
            if (!url || !error || !success) {
                _this._removeLoading();
                return;
            }
            var def = new toolbox.$.Deferred();
            var callback = _this._makeDesiredDataRequestCallback.bind(_this, def, error, success);
            var entityInstanceId = _this.getCurrentEntityInstanceId();
            _this._BSSBackendConnector.makeDataRequest(url, requestParams, callback, entityInstanceId);
            return def.promise();
        };
        /**
         * @desc feature specific functionality:
         * retrieve value of parameter provided by Italy integrator to call for lead generation form
         * @return promise, on resolve it returns calculated offer
         * @private
         */
        _this._getLeadGenerationData = function () {
            _this._showOverlay();
            var requestData = _this._getOfferDataForLeadGenerationCall();
            var url = requestData.url, params = requestData.params;
            if (!url) {
                console.log('Missing lead generation url');
                _this._removeLoading();
                return;
            }
            var def = new toolbox.$.Deferred();
            var callback = _this._getLeadGenerationDataCallback.bind(_this, url, def);
            var entityInstanceId = _this.getCurrentEntityInstanceId();
            _this._BSSBackendConnector.makeDataRequest(url, params, callback, entityInstanceId);
            return def.promise();
        };
        /**
         * Show 'minor' error popup
         * @param publisherName {string}
         * @param errorObj {{}} errorObject - error object
         * @private
         */
        _this._showMinorErrorPopup = function (publisherName, errorObj) {
            if (errorObj === void 0) { errorObj = {}; }
            var revertStateTitle = errorObj.errorTitle, revertStateMsg = errorObj.errorMsg;
            _this._showErrorPopup({
                revertStateTitle: revertStateTitle,
                revertStateMsg: revertStateMsg,
            }, errorObj);
            _this._hideOverlay();
        };
        _this._serveDevMode();
        return _this;
    }
    /**
     * Serve connector in dev mode if on
     * @private
     */
    BSSControllerToolbox.prototype._serveDevMode = function () {
        this._devMode = !!window.localStorage.getItem('BSS_SIM_DEV');
        logger.info('ControllerToolbox => DEV MODE: ', this._devMode);
    };
    /**
     * Return mocked url for base UC configuration
     * @private
     */
    BSSControllerToolbox.prototype._getMockUrl = function () {
        // use BSS_SIM_DEV to serve local json files
        if (this._devMode) {
            var envUrl = "".concat(process.env.ENV_URL, "/binaries/api");
            return "".concat(envUrl, "/").concat(this._getMockFileName());
        }
        return null;
    };
    /**
     * Return file name for mocked url
     * @private
     */
    BSSControllerToolbox.prototype._getMockFileName = function () {
        switch (this._flowId) {
            case '':
                return 'flowToyotaUCAT.json';
            case '72285': // QA AT UC mock flowId
                return 'flowUCBe.json';
            case '72287': // QA FI UC mock flowId
                return 'flowUCFi.json';
            case '22222': // DEV IT UC Toyota mock flowId
                return 'flowToyotaUCItDEV.json';
            case '33333': // QA IT UC Toyota mock flowId
                return 'flowToyotaUCItQA.json';
            case '44444': // UAT IT UC Toyota mock flowId
                return 'flowToyotaUCItUAT.json';
            case '55555': // PROD IT UC Toyota mock flowId
                return 'flowToyotaUCItPROD.json';
            case '66666': // DEV IT UC Lexus mock flowId
                return 'flowLexusUCItDEV.json';
            case '77777': // QA IT UC Lexus mock flowId
                return 'flowLexusUCItQA.json';
            case '88888': // UAT IT UC Lexus mock flowId
                return 'flowLexusUCItUAT.json';
            case '99999': // PROD IT UC Lexus mock flowId
                return 'flowLexusUCItPROD.json';
        }
    };
    /**
     * @desc simple setter for flow configuration
     * @param {Object} error - error object
     * @param {Object} data - flow configuration object
     * @emits {bss-flow-config-ready}
     * @listens {bss-create-flow-$FLOW-ID-entity-$ENTITY-ORDER}
     * @listens {bss-click-print-pdf-entity-$ENTITY-ORDER}
     * @public
     */
    BSSControllerToolbox.prototype.setFlowConfiguration = function (error, data) {
        if (!this._validateFlowId(data === null || data === void 0 ? void 0 : data.flowId))
            return;
        this._flowId = data.flowId;
        this._setFlowConfigurationCallback();
        if (this._checkRequestErrorAndData(error, data, 'setFlowConfiguration')) {
            // if there is no error and all data nodes exist
            this._flowConfiguration = this._setProtocol(data);
            this._initialEntityId = this._flowConfiguration.initialEntityId;
            // in multi-entity flow, add config for each entity
            if (this._flowConfiguration.entities &&
                Object.keys(this._flowConfiguration.entities).length) {
                var self_1 = this;
                toolbox._.forEach(this._flowConfiguration.entities, function (entityConfig, entityId) {
                    var urls = entityConfig.commonURLs;
                    self_1._flowEntities[entityId] = {
                        dataUrlBase: urls.entitySimulatorURL,
                        resultUrlBase: urls.entityDataURL,
                        textsUrlBase: urls.entityTextsAndFormattersURL,
                        layoutUrlBase: urls.entityLayoutURL,
                        calculationRawDataUrl: urls.entityCalculationRawDataURL,
                        dependencies: entityConfig.dependencies,
                        entitySelector: entityConfig.container,
                        entityContainer: toolbox.$(entityConfig.container),
                        configuration: {},
                        defaultOffer: {},
                        isDefaultOfferOutOfDate: true,
                        customOffer: {},
                        customOfferPrevious: {},
                        type: entityConfig.type,
                        order: entityConfig.order,
                        currentProdId: null,
                        initData: entityConfig.initData || null,
                    };
                    // add .bss-entity-container class to all entities in the flow
                    self_1._flowEntities[entityId].entityContainer.addClass('bss-entity-container');
                    // subscribe to public event for entity creation
                    self_1._eventManager.subscribe('bss-create-flow-' +
                        self_1._flowId +
                        '-entity-' +
                        entityConfig.order, self_1.setEntity.bind(self_1, entityConfig.order), 'ControllerToolbox');
                });
            }
            this._eventManager.publish('bss-flow-config-ready', UC_PUBLISHER_NAME, {}, true);
            // subscribe to public event for update finance after click close button
            this._eventManager.subscribe('bss-close-entity-' + this._flowId, this._closeUpdateFinance.bind(this), 'ControllerToolbox');
            this._eventManager.subscribe('bss-destroy-connector-event', this._clearModalPopupStyle.bind(this));
            this._eventManager.subscribe('bss-destroy-connector-event', this._clearModalPopupStyle.bind(this));
        }
    };
    /**
     * Clear modal popup additional classes
     * @private
     */
    BSSControllerToolbox.prototype._clearModalPopupStyle = function () {
        this._entityContainer.removeClass('bss-scroll');
    };
    /**
     * @desc set variable on true for finished tagging - BC
     * @public
     */
    BSSControllerToolbox.prototype._onFinishedTagging = function () {
        this._isTaggingFinished = true;
    };
    /**
     * @desc close update finance method
     * @public
     */
    BSSControllerToolbox.prototype._closeUpdateFinance = function () {
        var _a, _b;
        this._eventManager.publish('bss-close-entity-' + this._flowId, UC_PUBLISHER_NAME, this._financeObject, true);
        if (this.entityBuilder._entityMap.size > 0) {
            this._publishTaggingEvent(this._currentEntity._entityId, 'simulatorConfirmClosing_' +
                this.entityBuilder._entityMap.entries().next().value[1]
                    ._entityActiveProductId);
            this._isCloseButton = true;
            (_b = (_a = toolbox.dxpTagging) === null || _a === void 0 ? void 0 : _a[this._currentEntity._entityId]) === null || _b === void 0 ? void 0 : _b.publish('saveEntity', toolbox.resultDataFlat[toolbox.currentProductId]);
        }
        this._eventManager.publish('hide-build-amd-modal', UC_PUBLISHER_NAME, {}, true);
    };
    /**
     * NBSS-10113
     * Add additional class to entity container to differentiate styles when scroll appears,
     * eg for revert state popup message
     * @private
     */
    BSSControllerToolbox.prototype._addScrollClass = function () {
        var modalBodyEL = this._entityContainer.parents('.material-box')[0];
        if (modalBodyEL && modalBodyEL.clientHeight < modalBodyEL.scrollHeight) {
            this._entityContainer.addClass('bss-scroll');
        }
    };
    /**
     * @desc method replace @protocol@ with current protocol
     * @param data
     * @return {Object}
     * @private
     */
    BSSControllerToolbox.prototype._setProtocol = function (data) {
        return JSON.parse(JSON.stringify(data).replace(/@protocol@/g, this._protocol));
    };
    /**
     * @desc prepare for creating/showing entity with given order
     * @param {Number} entityOrder
     * @public
     */
    BSSControllerToolbox.prototype.setEntity = function (entityOrder) {
        // support for old config - single entity flow
        if (entityOrder === null) {
            this._createFlowEntity(this._initialEntityId);
            return;
        }
        // get entity id and type based on its order
        var entityId = false;
        toolbox._.forEach(this._flowEntities, function (entityData, entityIndex) {
            if (entityData.order === entityOrder) {
                entityId = entityIndex;
            }
        });
        // if there is no such entity, show error msg
        if (!entityId) {
            var errObj = {
                errorMsg: 'ControllerToolbox setEntity error: there is no entity with order ' +
                    entityOrder,
            };
            this._errorHandler(null, errObj);
            return;
        }
        // else create entity with given order
        this._entityToBeShown = entityId;
        // check if given entity already exists
        if (this._checkEntityExistence(entityId)) {
            this._showEntity(entityId);
            this._currentEntity._entityPrevProductId =
                this._currentEntity._entityActiveProductId;
            return;
        }
        // if entity was not created or was destroyed, create it
        this._createFlowEntity(entityId);
    };
    /**
     * @desc creates entity entity regarding to config and UIDescription (when entity builder js is loaded)
     * @param {String} entityId
     * @listens {bss-update-finance-$ENTITY-INSTANCE-ID}
     * @listens {bss-entity-critical-error-$CURRENT-ENTITY-ID}
     * @private
     */
    BSSControllerToolbox.prototype._createEntity = function (entityId) {
        this._setActiveEntity(entityId);
        // get default offer for entity
        if (toolbox.$.isEmptyObject(this.getDefaultOffer()) ||
            this._getIsDefaultOfferOutOfDate(entityId)) {
            this._calculateOfferWrapper(this._createEntity.bind(this, entityId));
            return;
        }
        var entityInstanceId = toolbox.$.now();
        var offer = this._currentData.resultData[this._flowEntities[this._currentEntityId].currentProdId];
        this._setFinanceObject(offer);
        this._onFinanceChange(offer);
        this._eventManager.subscribe('bss-update-finance-' + entityInstanceId, this._onUpdateFinanceEvent.bind(this), 'ControllerToolbox');
        this._eventManager.subscribe('bss-entity-critical-error-' + entityInstanceId, this._errorHandler.bind(this), 'ControllerToolbox');
        // add flow params to toolbox resultDataFlat for possible future needs e.g. in tagging
        toolbox.resultDataFlat[this._flowEntities[entityId].currentProdId].flowParams = this._flowParams;
        // check if entity builder exists - if not, create it
        if (!this.entityBuilder) {
            this.entityBuilder = new EntityBuilder(this._eventManager, this._formatter, this._resolutions, toolbox, this._components, 'uc');
        }
        this.entityBuilder
            .createEntity(this._flowEntities[entityId].entitySelector, entityId, this._flowEntities[entityId].configuration, entityInstanceId, 'uc')
            .then(this._onEntityCreated.bind(this));
    };
    /**
     * @desc gets and sets locally calculated default offer and initial state of custom offer
     * @param {Function} [callback = null]
     * @private
     */
    BSSControllerToolbox.prototype._calculateOfferWrapper = function (callback) {
        if (callback === void 0) { callback = null; }
        var entityId = this._currentEntityId;
        var self = this;
        this._checkEntityId(entityId);
        this.checkFlowConfiguration();
        this._calculateOffer(true, entityId).then(function (data) {
            var activeEntity = self._flowEntities[entityId];
            var resulData = data.resultData;
            var resultLengthCheck = Object.keys(resulData);
            if (!activeEntity.currentProdId &&
                Object.hasOwn(data, 'resultData') &&
                resultLengthCheck.length) {
                for (var i = 0; i < resultLengthCheck.length; i += 1) {
                    if (Object.hasOwn(resulData[resultLengthCheck[i]], 'activeTabId')) {
                        activeEntity.currentProdId =
                            resulData[resultLengthCheck[i]].activeTabId;
                    }
                }
                if (!activeEntity.currentProdId) {
                    activeEntity.currentProdId = resultLengthCheck[0];
                }
            }
            self._setDefaultOffer(entityId, data);
            self._setCustomOffer(entityId, data);
            self._setEntityConfiguration(entityId, data);
            if (callback !== null) {
                try {
                    callback(entityId);
                }
                catch (err) {
                    var errObj = {
                        entityInstanceId: self.getCurrentEntityInstanceId(),
                        errorMsg: '',
                    };
                    this._handleErrorsForCalculateOfferFunction(callback, errObj, err, self);
                }
            }
        });
    };
    BSSControllerToolbox.prototype._handleErrorsForCalculateOfferFunction = function (callback, errObj, err, self) {
        if (typeof callback !== 'function') {
            errObj.errorMsg =
                'Controller calculateOffer error: The calculateOffer callback is not a function - ' +
                    err;
            self._errorHandler(null, errObj);
        }
        else {
            errObj.errorMsg =
                'Controller calculateOffer error: During calculateOffer callback unhandled exception caught - ' +
                    err;
            self._errorHandler(null, errObj);
        }
    };
    /**
     * @desc set entity configuration
     * @param {String} entityId - entity id
     * @param {Object} data - flow configuration object
     * @private
     */
    BSSControllerToolbox.prototype._setEntityConfiguration = function (entityId, data) {
        try {
            var config = data;
            if (config.layout && config.layout.formatters) {
                this._formatter = new Formatter(config.layout.formatters);
            }
            else {
                var currentFormatter = {};
                this._formatter = new Formatter(currentFormatter);
            }
            if (config.layout) {
                config.layout.theme = this._options.theme;
            }
            this._flowEntities[entityId].configuration = config;
            this._cacheLayoutTexts(config);
        }
        catch (e) {
            var errObj = {
                entityInstanceId: this.getCurrentEntityInstanceId(),
                errorMsg: 'ControllerToolbox _setEntityConfiguration error - ' + e,
            };
            this._errorHandler(null, errObj);
        }
    };
    /**
     * @desc publish public event for finance change
     * @param {Object} data - current changed data
     * @emits {bbss_finance_change}
     * @private
     */
    BSSControllerToolbox.prototype._onFinanceChange = function (data) {
        var countriesForFinanceChange = ['pl'];
        if (countriesForFinanceChange.includes(this._flowParams.country)) {
            var downpayment = data.downpayment, downpaymentPercentage = data.downpaymentPercentage, duration = data.duration, disclaimer = data.disclaimer, monthlyPayment = data.monthlyPayment, productCustomName = data.productCustomName, quoteId = data.quoteId;
            var financeData = {
                ownPayment: downpayment,
                ownPaymentPercent: downpaymentPercentage,
                period: duration,
                legal: disclaimer,
                installment: monthlyPayment,
                productCustomName: productCustomName,
                financeQuoteId: quoteId,
            };
            if (data.repurchase) {
                financeData.repurchase = data.repurchase;
            }
            if (data.balloon) {
                financeData.balloonPayment = data.balloon;
            }
            if (data.mileage) {
                financeData.annualMileage = data.mileage;
            }
            this._eventManager.publish('bss_finance_change', UC_PUBLISHER_NAME, financeData, true);
        }
    };
    /**
     * @desc retrieve value of parameter provided by integrator, user or calculated
     * @param {String} publishername - publisher name
     * @param {Object} data - data provided with event, data from component updateted after user change
     * @emits {bss-show-entity-overlay-$ENTITY-INSTANCE-ID}
     * @emits {bss-data-received-$ENTITY-INSTANCE-ID}
     * @emits {bss-hide-entity-overlay-$ENTITY-INSTANCE-ID}
     * @emits {bss-send-request-$ENTITY-INSTANCE-ID}
     * @emits {bss-received-request-$ENTITY-INSTANCE-ID}
     * @return promise, on resolve it returns calculated offer
     * @private
     */
    BSSControllerToolbox.prototype._onUserChange = function (publishername, data) {
        var _this = this;
        var entityInstanceId = this.getCurrentEntityInstanceId();
        this._eventManager.publish('bss-send-request-' + entityInstanceId, UC_PUBLISHER_NAME, {});
        var depValues = this._getEntityDependenciesValues(this._currentEntityId);
        var currentData = data[this._currentEntity._entityActiveProductId];
        if (this._options &&
            this._options.clearDependentVariables &&
            this._currentValueChange) {
            for (var item in this._options.clearDependentVariables) {
                if (this._options.clearDependentVariables[item].fields.indexOf(this._currentValueChange) !== -1) {
                    currentData[item] = this._options.clearDependentVariables[item].value;
                }
            }
        }
        var _loop_1 = function (item) {
            /* eslint-disable */
            depValues = this_1._updateEntityDependenciesValues(depValues, data[item]);
            this_1._updateComponentsVals(data[item], depValues);
            try {
                this_1._saveCustomOfferPrevious(this_1._currentEntityId);
                this_1._eventManager.publish('bss-show-entity-overlay-' + entityInstanceId, UC_PUBLISHER_NAME, this_1.getCustomOffer());
                if (toolbox._.has(data[item], 'quoteId')) {
                    delete data[item].quoteId;
                }
                this_1._calculateOffer(false, item, data[item]).then(function (currentOffer) {
                    // NBSS-10206
                    var offer = currentOffer[item];
                    if (!toolbox._.has(offer, 'maintenanceType')) {
                        offer.maintenanceType = '';
                    }
                    _this._setCustomOffer(_this._currentEntityId, currentOffer);
                    currentOffer.updateTimestamp = new Date().getTime();
                    currentOffer.offerId = item;
                    _this._setFinanceObject(offer);
                    _this._onFinanceChange(offer);
                    _this._eventManager.publish('bss-data-received-' + entityInstanceId, UC_PUBLISHER_NAME, currentOffer);
                    _this._eventManager.publish('bss-hide-entity-overlay-' + entityInstanceId, UC_PUBLISHER_NAME, currentOffer);
                    _this._eventManager.publish('bss-received-request-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                });
            }
            catch (err) {
                var updateObj = this_1.getCustomOffer();
                updateObj.updateTimestamp = new Date().getTime();
                this_1._eventManager.publish('bss-data-received-' + entityInstanceId, UC_PUBLISHER_NAME, updateObj);
                this_1._eventManager.publish('bss-hide-entity-overlay-' + entityInstanceId, UC_PUBLISHER_NAME, this_1.getCustomOffer());
                this_1._eventManager.publish('bss-received-request-' + entityInstanceId, UC_PUBLISHER_NAME, {});
            }
        };
        var this_1 = this;
        for (var item in data) {
            _loop_1(item);
        }
    };
    /**
     * Get calculation offer data to be sent to BE on financial data change
     * @param offerId {string}
     * @param tempCustomOffer {{}}
     * @returns {{params: {}, resultUrl: string, textsUrl: string}}
     * @private
     */
    BSSControllerToolbox.prototype._getCalculationOfferData = function (offerId, tempCustomOffer) {
        var textsUrl = '';
        var resultUrl = '';
        var params = {};
        var currentProdId = this._getCurrentProdId();
        var depValues = this._getEntityDependenciesValues(this._currentEntityId);
        textsUrl = this._flowEntities[this._currentEntityId].textsUrlBase
            .replace(UC_SIMULATOR_ID, this._currentEntityId)
            .replace('{offerId}', currentProdId)
            .replace('{language}', this._flowProps.lang);
        resultUrl = this._flowEntities[this._currentEntityId].resultUrlBase
            .replace(UC_SIMULATOR_ID, this._currentEntityId)
            .replace('{offerId}', currentProdId);
        // simulator load - no default offer
        if (this._isEmptyDefaultOffer()) {
            var paramsObj = toolbox.$.extend({ language: this._flowProps.lang }, this._flowParams, depValues);
            params = JSON.stringify(paramsObj);
            // recalculation - second and next requests
        }
        else {
            var flowParams = this._flowParams;
            if (flowParams.elementType) {
                delete flowParams.elementType;
            }
            if (flowParams.parameters) {
                delete flowParams.parameters;
            }
            flowParams.financial = {};
            if (this._options && this._options.sendToRequest) {
                for (var _i = 0, _a = this._options.sendToRequest; _i < _a.length; _i++) {
                    var item = _a[_i];
                    if (item === 'services' && this._options.services) {
                        this._flowParams.financial[item] = [];
                        for (var _b = 0, _c = this._options.services; _b < _c.length; _b++) {
                            var service = _c[_b];
                            if (Object.hasOwn(tempCustomOffer, service + 'ServiceCode') &&
                                JSON.parse(tempCustomOffer[service + 'Selected'])) {
                                var code = service + 'ServiceCode';
                                var type = service + 'ServiceType';
                                this._flowParams.financial[item].push({
                                    serviceCode: tempCustomOffer[code],
                                    servTypeCode: tempCustomOffer[type],
                                });
                            }
                        }
                    }
                    else if (item === 'downpayment') {
                        this._flowParams.financial[item] =
                            typeof tempCustomOffer[item] !== 'undefined'
                                ? tempCustomOffer[item]
                                : tempCustomOffer['firstInstalmentAmount'];
                    }
                    else if (item === 'mileage') {
                        this._flowParams.financial[item] =
                            tempCustomOffer['contractMileage'];
                    }
                    else {
                        if (Object.hasOwn(tempCustomOffer, item)) {
                            this._flowParams.financial[item] = tempCustomOffer[item];
                        }
                    }
                }
            }
            else {
                flowParams.financial = tempCustomOffer;
                flowParams.financial.productId = offerId;
            }
            params = toolbox.$.extend({ language: this._flowProps.lang }, flowParams, tempCustomOffer, depValues);
            var country = (params || {}).country;
            if (country === 'fi') {
                Object.assign(params.financial, tempCustomOffer);
                var financial_1 = params.financial;
                var tempDeductibleObject = {};
                var tempMaintenanceObject = {};
                for (var key in financial_1) {
                    if (key.startsWith('deductible')) {
                        tempDeductibleObject[key] = financial_1[key];
                        delete params.financial[key];
                    }
                }
                params.financial.deductible = tempDeductibleObject;
                for (var key in financial_1) {
                    if (key.startsWith('maint')) {
                        tempMaintenanceObject[key] = financial_1[key];
                        delete params.financial[key];
                    }
                }
                params.financial.maintenance = tempMaintenanceObject;
                var arrayWithKeysToMove = [
                    'laskeHintaClicked',
                    'insuranceSelected',
                    'insuranceVisible',
                    'birthDate',
                    'postalCode',
                ];
                var tempCustomObject_1 = {};
                var _loop_2 = function (key) {
                    arrayWithKeysToMove.forEach(function (el) {
                        if (key === el) {
                            tempCustomObject_1[key] = financial_1[key];
                            delete params.financial[key];
                        }
                    });
                };
                for (var key in financial_1) {
                    _loop_2(key);
                }
                Object.assign(params.financial.deductible, tempCustomObject_1);
            }
            if (country === 'it') {
                Object.assign(params.financial, tempCustomOffer);
                var financial_2 = params.financial;
                var tempInsuranceObject = {};
                var tempMaintenanceObject = {};
                for (var key in financial_2) {
                    if (Object.hasOwn(financial_2, key) && key.startsWith('ftInsurance')) {
                        tempInsuranceObject[key] = financial_2[key];
                        delete params.financial[key];
                    }
                }
                params.financial.itInsurance = tempInsuranceObject;
                for (var key in financial_2) {
                    if (Object.hasOwn(financial_2, key) && key.startsWith('maintenance')) {
                        tempMaintenanceObject[key] = financial_2[key];
                        delete params.financial[key];
                    }
                }
                params.financial.itMaintenance = tempMaintenanceObject;
                var arrayWithKeysToMove = [
                    'calculateQuoteId',
                    'isCcGetRequest',
                    'zipCode',
                ];
                var tempCustomObject_2 = {};
                var _loop_3 = function (key) {
                    if (Object.hasOwn(financial_2, key)) {
                        arrayWithKeysToMove.forEach(function (el) {
                            if (key === el) {
                                tempCustomObject_2[key] = financial_2[key];
                                delete params.financial[key];
                            }
                        });
                    }
                };
                for (var key in financial_2) {
                    _loop_3(key);
                }
                params.financial.itParameters = tempCustomObject_2;
            }
        }
        return { textsUrl: textsUrl, resultUrl: resultUrl, params: params };
    };
    /**
     * Flag stating if a default offer is empty
     * @private
     */
    BSSControllerToolbox.prototype._isEmptyDefaultOffer = function () {
        return toolbox.$.isEmptyObject(this.getDefaultOffer());
    };
    /**
     * Callback on BE response after financial data change
     * @param error {{}}
     * @param res {{}}
     * @param checkInnerNodes {boolean}
     * @param offerId {string}
     * @param def {Promise}
     */
    BSSControllerToolbox.prototype._validateOfferCallback = function (checkInnerNodes, offerId, def, error, res) {
        var data = this.parseRequest(res);
        var entityInstanceId = this.getCurrentEntityInstanceId();
        var isValid = false;
        this._noErrorPopup = true;
        if (this._checkRequestErrorAndData(error, data, 'getCalculatedOffer', checkInnerNodes, offerId, false)) {
            isValid = true;
            // Wait until user will confirm validated state
            // eg by clicking on 'Confirm' button
            this._eventManager.subscribe("bss-validated-state-confirm-".concat(entityInstanceId), 
            // Apply validated offer (standard offer calculation logic)
            this._calculateOfferCallback.bind(this, checkInnerNodes, offerId, def, error, res), 'ControllerToolboxTabClick');
        }
        else {
            // if error occurred
            def.resolve({});
        }
        var validatedFinanceOfferEventName = "bss-params-validated-".concat(entityInstanceId);
        // Tell component that validation is completed with success
        this._eventManager.publish(validatedFinanceOfferEventName, UC_PUBLISHER_NAME, { isValid: isValid });
        this._removeLoading();
        this._noErrorPopup = false;
    };
    /**
     * Callback on BE response when default offer is initially created
     * @param checkInnerNodes {boolean}
     * @param offerId {string}
     * @param def {Promise}
     * @param {Record<string, any>} resultText texts object from BE
     * @param {Record<string, any>} resultData financial data object from BE
     */
    BSSControllerToolbox.prototype._calculateDefaultOfferCallback = function (checkInnerNodes, offerId, def, resultText, resultData) {
        var _a;
        this._currentData = { layout: {}, resultData: {} };
        var texts = {};
        // texts & formatters
        Object.assign(texts, { general: resultText.generalTexts });
        for (var _i = 0, _b = resultText.texts; _i < _b.length; _i++) {
            var text = _b[_i];
            var productId = text.reference;
            texts[productId] = text.texts;
        }
        this._currentData.layout['texts'] = texts;
        this._currentData.layout['formatters'] = resultText.formatters;
        //resultData
        if (resultData) {
            for (var index in resultData) {
                if (!((_a = resultData[index]) === null || _a === void 0 ? void 0 : _a.productId)) {
                    delete resultData[index]; // REMOVES ERRORED PRODUCTS TO SHOW SIMULATOR
                }
            }
            Object.assign(this._currentData.resultData, resultData);
            this._currentData = this.parseRequest(this._currentData);
        }
        if (this._checkRequestErrorAndData(null, this._currentData, 'getCalculatedOffer', checkInnerNodes, offerId)) {
            def.resolve(this._currentData);
        }
    };
    /**
     * @desc retrieve value of parameter provided by integrator, user or calculated
     * @param {Boolean} checkInnerNodes - if true check weather inner nodes exists (false on initialization for calculateOffer function)
     * @param {String} offerId - type of offer
     * @param {Object} tempCustomOffer
     * @return promise, on resolve it returns calculated offer
     * @private
     */
    BSSControllerToolbox.prototype._calculateOffer = function (checkInnerNodes, offerId, tempCustomOffer) {
        var _this = this;
        //delete error list from data object
        if (tempCustomOffer && Object.hasOwn(tempCustomOffer, 'errors')) {
            delete tempCustomOffer.errors;
        }
        var def = new toolbox.$.Deferred();
        var entityInstanceId = this.getCurrentEntityInstanceId();
        var _a = this._getCalculationOfferData(offerId, tempCustomOffer), textsUrl = _a.textsUrl, resultUrl = _a.resultUrl, params = _a.params;
        // simulator load - no default offer
        if (this._isEmptyDefaultOffer()) {
            var requestText = this._BSSBackendConnector._makeRequestUC('GET', textsUrl, {}, entityInstanceId);
            var requestResultData = this._BSSBackendConnector._makeRequestUC('POST', resultUrl, params, entityInstanceId);
            Promise.allSettled([requestText, requestResultData])
                .then(function (_a) {
                var resultText = _a[0], resultData = _a[1];
                _this._calculateDefaultOfferCallback(checkInnerNodes, offerId, def, resultText.value, resultData.value);
            })
                .catch(function (err) {
                _this._checkRequestErrorAndData(err, _this._currentData, 'getCalculatedOffer', checkInnerNodes, offerId);
            });
            // recalculation - second and next requests
        }
        else {
            // Use callback for state validation functionality (initially used on IT monthly rates popup)
            // otherwise use base calculation offer functionality (update financial data)
            var callback = params.validateState
                ? this._validateOfferCallback.bind(this, checkInnerNodes, offerId, def)
                : this._calculateOfferCallback.bind(this, checkInnerNodes, offerId, def);
            this._BSSBackendConnector.getCalculatedOffer(resultUrl, params, callback, entityInstanceId);
        }
        return def.promise();
    };
    /**
     * Callback on BE response after financial data change
     * @param error {{}}
     * @param res {{}}
     * @param checkInnerNodes {boolean}
     * @param offerId {string}
     * @param def {Promise}
     */
    BSSControllerToolbox.prototype._calculateOfferCallback = function (checkInnerNodes, offerId, def, error, res) {
        var data = this.parseRequest(res);
        var currentProductId = this._currentEntity._entityActiveProductId;
        if (this._checkRequestErrorAndData(error, data, 'getCalculatedOffer', checkInnerNodes, offerId)) {
            if (data && data[offerId] && data[offerId].quoteId) {
                var event_1 = new CustomEvent('BSSQuoteID', {
                    bubbles: true,
                    detail: { quoteId: data[offerId].quoteId },
                });
                document.dispatchEvent(event_1);
            }
            def.resolve(data);
        }
    };
    BSSControllerToolbox.prototype.parseRequest = function (data) {
        var elementType = this._flowEntities[this._currentEntityId].type;
        var brand = this._flowParams.brand;
        var country = this._flowParams.country;
        if (country === 'fi') {
            return this.parseRequestWithInsurance(data, elementType, brand, country);
        }
        else {
            return parseRequest({
                res: data,
                elementType: elementType,
                brand: brand,
                country: country,
                descriptionConfig: this.descriptionConfig,
                flowId: this._flowId,
                entityId: this.getCurrentEntityId(),
                flowParams: this._flowParams,
            });
        }
    };
    /**
     * If the insurance is already calculated in insurance calculator,
     * then the finance insurance should be blocked (Laske Hinta)
     * @desc only for UC FI
     * @param data
     * @param elementType
     * @param brand
     * @param country
     */
    BSSControllerToolbox.prototype.parseRequestWithInsurance = function (data, elementType, brand, country) {
        var _a, _b;
        var insuranceQuoteId = ((_b = (_a = this._flowParams) === null || _a === void 0 ? void 0 : _a.parameters) === null || _b === void 0 ? void 0 : _b.insuranceQuoteId) || null;
        return parseRequest({
            res: data,
            elementType: elementType,
            brand: brand,
            country: country,
            descriptionConfig: this.descriptionConfig,
            flowId: this._flowId,
            entityId: this.getCurrentEntityId(),
            insuranceQuoteId: insuranceQuoteId,
            flowParams: this._flowParams,
        });
    };
    /**
     * @desc Add query params to URL with encodeURIComponent
     * @param baseUrl
     * @param params
     * @returns {string}
     * @private
     */
    BSSControllerToolbox.prototype._toUrl = function (baseUrl, params) {
        var url = encodeURI(baseUrl);
        var i = url.indexOf('?') === -1 ? 0 : 1;
        for (var k in params) {
            if (Object.hasOwn(params, k)) {
                if (i === 0) {
                    url += '?';
                }
                else {
                    url += '&';
                }
                url = url + k + '=' + encodeURIComponent(params[k]);
                i++;
            }
        }
        return url;
    };
    /**
     * @desc callback on entity created
     * @param {Object} data
     * @emits {bss-flow-created-$FLOW-ID} public event
     * @emits {bss-flow-created-$FLOW-ID-entity-$ENTITY-ID-created} public event
     * @emits {bss-flow-created-$FLOW-ID-entity-$ENTITY-ORDER-created} public event
     * @emits {bss-fonts-ready-$ENTITY-INSTANCE-ID} public event
     * @emits {bss-additional-config-apply} public event
     * @listens {bss-params-changed-$ENTITY-INSTANCE-ID}
     * @listens {bss-data-changed-$ENTITY-INSTANCE-ID}
     * @listens {bss-component-critical-error-$ENTITY-INSTANCE-ID}
     * @listens {bss-entity-prod-change-$ENTITY-INSTANCE-ID}
     * @private
     */
    BSSControllerToolbox.prototype._onEntityCreated = function (data) {
        var _a, _b, _c;
        var entityId = data.entityId;
        var entityOrder = this._getEntityOrder(entityId);
        var entityInstanceId;
        var font = new FontFaceObserver('Open Sans Regular');
        var self = this;
        this._setActiveEntity(entityId);
        entityInstanceId = this.getCurrentEntityInstanceId();
        this._entityCounter++;
        // show entity only when is not the one from dependencies
        if (entityId === this._entityToBeShown) {
            this._showEntity(entityId);
            this._entityToBeShown = null;
        }
        // set current product id
        if (this._flowEntities[entityId].type === 'simulator') {
            this._flowEntities[entityId].currentProdId =
                this.entityBuilder.entities.get(entityId).entityActiveProductId;
        }
        else if (this.entityBuilder.entities.size > 1) {
            this._flowEntities[entityId].currentProdId =
                this.entityBuilder.entities.get(this._prevEntityId).entityActiveProductId;
        }
        else if (!this._currentEntity._entityActiveProductId) {
            // if entityActiveProductId === null & this._flowEntities[entityId].type !== 'simulator'
            this._currentEntity._entityActiveProductId =
                this._flowEntities[entityId].currentProdId;
        }
        this._eventManager.publish('bss-flow-' + this._flowId + '-entity-' + entityId + '-created', UC_PUBLISHER_NAME, {}, true);
        this._eventManager.publish('bss-flow-' + this._flowId + '-entity-' + entityOrder + '-created', UC_PUBLISHER_NAME, {}, true);
        this._eventManager.publish('bss-entity-created-' + this._flowId, UC_PUBLISHER_NAME, {}, true);
        this._addScrollClass();
        this._addEntityLocationClass();
        this._eventManager.publish('bss-additional-config-apply', UC_PUBLISHER_NAME, {}, true);
        this._eventManager.subscribe('bss-params-changed-' + entityInstanceId, this._onParamsChange.bind(this), 'ControllerToolbox');
        this._eventManager.subscribe('bss-data-changed-' + entityInstanceId, this._onUserChange.bind(this), 'ControllerToolbox');
        this._eventManager.subscribe('bss-component-critical-error-' + entityInstanceId, this._componentErrorHandler.bind(this), 'ControllerToolbox');
        this._eventManager.subscribe('bss-entity-prod-change-' + entityInstanceId, this._onProductChange.bind(this), 'ControllerToolbox');
        this._eventManager.subscribe("bss-get-lead-generation-data-".concat(entityInstanceId), this._getLeadGenerationData, 'ControllerToolbox');
        this._eventManager.subscribe("bss-component-minor-error-".concat(entityInstanceId), this._showMinorErrorPopup, 'ControllerToolbox');
        this._eventManager.subscribe("bss-connector-recived-desired-data-".concat(entityInstanceId), this._makeDesiredDataRequest, 'ControllerToolbox');
        this._eventManager.publish("bss-data-change-ready-".concat(entityInstanceId), UC_PUBLISHER_NAME, {});
        this._publishTaggingEvent(entityId, 'simulatorHasFullyLoaded');
        (_b = (_a = toolbox.dxpTagging) === null || _a === void 0 ? void 0 : _a[entityId]) === null || _b === void 0 ? void 0 : _b.init(__assign(__assign({}, toolbox.resultDataFlat[this._flowEntities[entityId].currentProdId]), { integrationMode: ((_c = this._options) === null || _c === void 0 ? void 0 : _c.integrationMode) || null }));
        font
            .load()
            .then(function (el) {
            logger.info('ControllerToolbox => function: _onEntityCreated | then | Params: ', el);
            self._eventManager.publish('bss-fonts-ready-' + entityInstanceId, UC_PUBLISHER_NAME, {}, true);
        })
            .catch(function (el) {
            logger.warn('ControllerToolbox => function: _onEntityCreated | catch | Params: ', el);
        });
    };
    /**
     * @desc Add location class to enable styling of in-page simulator (standalone version)
     * without interfering with elements using older injection mechanism (popup version).
     * Class will be added only when location exists in initial configuration.
     * Class should be added on entity container in each case:
     * - when initial error occurs and error template is rendered
     * - when entity is rendered with success
     * @param {jQuery | HTMLElement} [$entityContainer = this._entityContainer] reference to the entity container element
     * @private
     */
    BSSControllerToolbox.prototype._addEntityLocationClass = function ($entityContainer) {
        if ($entityContainer === void 0) { $entityContainer = this._entityContainer; }
        var location = toolbox.location;
        if (location) {
            $entityContainer.addClass("bss-entity-container--location-".concat(location));
        }
    };
    /**
     * @desc handle general simulator request callback from server, eg
     * - to Callback on BE response after call for save on close Italy market
     * @param def {Promise}
     * @param errorCallback {Function}
     * @param successCallback {Function}
     * @param error {{}}
     * @param res {{}}
     */
    BSSControllerToolbox.prototype._makeDesiredDataRequestCallback = function (def, errorCallback, successCallback, error, res) {
        var desiredData = this.parseRequest(res);
        var entityInstanceId = this.getCurrentEntityInstanceId();
        if (error || !desiredData) {
            if (errorCallback) {
                errorCallback();
            }
            else {
                this._showLeadGenerationError();
            }
        }
        else {
            var currentProdId = this._flowEntities[this._currentEntityId].currentProdId;
            this._eventManager.publish("bss-connector-received-data-".concat(currentProdId, "-").concat(entityInstanceId), UC_PUBLISHER_NAME, {
                desiredData: desiredData,
            });
            successCallback();
        }
        this._hideOverlay();
        def.resolve(desiredData);
    };
    /**
     * Remove loading overlay
     * @private
     */
    BSSControllerToolbox.prototype._removeLoading = function () {
        var $currentEntityContainer = this._flowEntities[this._currentEntityId].entityContainer;
        $currentEntityContainer
            .find('.bss-entity-overlay')
            .removeClass('is-active');
    };
    /**
     * Get offer data for to be sent to BE on 'desired' data call,
     * used to call for Lead Generation on Italy market
     * @returns {{params: {}, url: string}}
     * @private
     */
    BSSControllerToolbox.prototype._getOfferDataForLeadGenerationCall = function () {
        var params = {};
        var country = (this._flowParams || {}).country;
        var depValues = this._getEntityDependenciesValues(this._currentEntityId);
        var currentEntity = this._flowEntities[this._currentEntityId];
        var currentProdId = currentEntity.currentProdId, _a = currentEntity.calculationRawDataUrl, calculationRawDataUrl = _a === void 0 ? '' : _a;
        // Product id is gotten by BE from calculateQuoteId
        var url = calculationRawDataUrl.replace(UC_SIMULATOR_ID, this._currentEntityId);
        var calculateQuoteId = currentEntity.customOffer.resultData[currentProdId].calculateQuoteId;
        params = toolbox.$.extend({
            language: this._flowProps.lang,
        }, this._flowParams, depValues);
        if (country === 'it') {
            params = toolbox.$.extend(params, {
                financial: {
                    productId: currentProdId,
                    itParameters: {
                        calculateQuoteId: calculateQuoteId,
                    },
                },
            });
        }
        return { url: url, params: params };
    };
    /**
     * Callback on BE response after call for financial data needed for Lead Generation call (desired data) on Italy
     * @param url {string}
     * @param def {Promise}
     * @param error {{}}
     * @param res {{}}
     */
    BSSControllerToolbox.prototype._getLeadGenerationDataCallback = function (url, def, error, res) {
        var desiredData = this.parseRequest(res);
        var entityInstanceId = this.getCurrentEntityInstanceId();
        if (error || !desiredData) {
            this._showLeadGenerationError();
            this._hideOverlay();
        }
        else {
            var currentProdId = this._flowEntities[this._currentEntityId].currentProdId;
            this._eventManager.publish("bss-connector-desired-data-".concat(currentProdId, "-").concat(entityInstanceId), UC_PUBLISHER_NAME, {
                desiredData: desiredData,
            });
        }
        def.resolve(desiredData);
    };
    /**
     * Show error popup on lead generation error response
     * @private
     */
    BSSControllerToolbox.prototype._showLeadGenerationError = function () {
        var errObj = {
            entityInstanceId: this.getCurrentEntityInstanceId(),
            errorCode: 'LEAD_GENERATION_ERROR',
        };
        var _a = this._flowEntities[this._currentEntityId].configuration.layout.texts.general, _b = _a.error_lead_generation_title, revertStateTitle = _b === void 0 ? 'Something went wrong during lead generation call' : _b, _c = _a.error_lead_generation_msg, revertStateMsg = _c === void 0 ? 'Please, try again later' : _c;
        this._showErrorPopup({
            revertStateTitle: revertStateTitle,
            revertStateMsg: revertStateMsg,
        }, errObj);
    };
    /**
     * Show general error popup
     * @param {{revertStateTitle: string, revertStateMsg: string}} revertTemplateConfig - config containing data for entity-revert-info-component
     * @param {Object} errorObject - error object
     * @private
     */
    BSSControllerToolbox.prototype._showErrorPopup = function (revertTemplateConfig, errorObject) {
        var isNoAmd = !this._entityContainer.hasClass('bss-amd');
        var entityHeight = toolbox
            .$(this._currentEntity._entitySelector)
            .outerHeight();
        // show snackbar with information about the error and state revert - from config.texts.general
        var $simulatorRevertTemplate = toolbox.$(simulatorTemplates.revertTemplate(revertTemplateConfig));
        var revertTemplateHeight = $simulatorRevertTemplate.outerHeight();
        var $currentEntityContainer = this._flowEntities[this._currentEntityId].entityContainer;
        var $overlayEl = $currentEntityContainer.find('.bss-popup_overlay');
        $overlayEl.addClass('is-active');
        $currentEntityContainer.append($simulatorRevertTemplate);
        var $closeButton = $simulatorRevertTemplate.find('.bss-entity-revert-state-info-close-icon');
        // Remove error popup on close button click
        var closeErrorPopup = function () {
            $overlayEl.removeClass('is-active');
            $simulatorRevertTemplate.fadeOut(400, function () {
                $currentEntityContainer.find('.bss-entity-revert-state-info').remove();
            });
            $overlayEl.off('click', closeErrorPopup);
        };
        $closeButton.on('click', closeErrorPopup);
        $overlayEl.on('click', closeErrorPopup);
        if (isNoAmd) {
            var componentPositionTop = typeof errorObject.componentId !== 'undefined'
                ? toolbox.$('#' + errorObject.componentId).offset().top
                : 0;
            var entityMiddlePos = entityHeight / 2 - revertTemplateHeight / 2;
            var top_1 = typeof errorObject.componentId !== 'undefined'
                ? componentPositionTop
                : entityMiddlePos;
            $simulatorRevertTemplate.css({ top: top_1 + 'px' });
        }
    };
    /**
     * Hide element overlay
     * @private
     */
    BSSControllerToolbox.prototype._showOverlay = function () {
        var entityInstanceId = this.getCurrentEntityInstanceId();
        this._eventManager.publish("bss-show-entity-overlay-".concat(entityInstanceId), UC_PUBLISHER_NAME);
    };
    /**
     * Hide element overlay
     * @private
     */
    BSSControllerToolbox.prototype._hideOverlay = function () {
        var entityInstanceId = this.getCurrentEntityInstanceId();
        this._eventManager.publish("bss-hide-entity-overlay-".concat(entityInstanceId), UC_PUBLISHER_NAME);
    };
    /**
     * @desc show entity with given id
     * @param {String} currentEntityId
     * @private
     */
    BSSControllerToolbox.prototype._showEntity = function (currentEntityId) {
        var self = this;
        var entityContainer;
        var entityObj;
        var entityInstanceId;
        var event;
        toolbox._.forEach(this._flowEntities, function (entityData, entityId) {
            entityContainer = entityData.entityContainer;
            entityObj = self.entityBuilder.entities.get(entityId);
            // if this entity is not created yet - add proper class and return
            if (typeof entityObj === 'undefined') {
                entityContainer.addClass(HIDDEN_CLASS).hide();
                return;
            }
            entityInstanceId = entityObj.entityInstanceId;
            if (entityId === currentEntityId) {
                // start resize and scroll events from new entity
                self._eventManager.publish('bss-start-resize-for-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                self._eventManager.publish('bss-start-scroll-for-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                // show new entity
                entityContainer.removeClass(HIDDEN_CLASS).show(0, function () {
                    self._eventManager.publish('bss-entity-is-shown-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                });
                event = new Event('resize');
                window.dispatchEvent(event);
            }
            else if (!entityContainer.hasClass(HIDDEN_CLASS)) {
                // close slide-up layer, stop resize and scroll events from old entities
                self._eventManager.publish('bss-stop-resize-for-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                self._eventManager.publish('bss-stop-scroll-for-' + entityInstanceId, UC_PUBLISHER_NAME, {});
                // hide old entities
                entityContainer.addClass(HIDDEN_CLASS).hide();
            }
        });
        this._setActiveEntity(currentEntityId);
    };
    /**
     * @desc gets text for chosen language
     * @param {String} publisherName - publisher name
     * @private
     */
    BSSControllerToolbox.prototype._getTexts = function () {
        var textsUrl = this._flowEntities[this._currentEntityId].textsUrlBase.replace('{entityId}', this._currentEntityId);
        var entityInstanceId = this.getCurrentEntityInstanceId();
        this._BSSBackendConnector.getTexts(textsUrl, this._textReceived.bind(this), entityInstanceId);
    };
    /**
     * @desc gets text callback
     * @param {Object} error
     * @param {Object} data
     * @emits {bss-text-received-$ENTITY-INSTANCE-ID} when there is no error and data nodes exist
     * @private
     */
    BSSControllerToolbox.prototype._textReceived = function (error, data) {
        // if there is no error and all data nodes exist
        if (this._checkRequestErrorAndData(error, data, 'textReceived')) {
            var entityInstanceId = this.getCurrentEntityInstanceId();
            this._eventManager.publish('bss-text-received-' + entityInstanceId, UC_PUBLISHER_NAME, data);
        }
    };
    /**
     * @desc check error and data received after data request
     * @param {Object} error - error object
     * @param {Object} data - flow configuration object
     * @param {String} funName - the caller function name
     * @param {Boolean} [checkInnerNodes=true] - if true check weather inner nodes exists (false on initialization for calculateOffer function)
     * @param {String} [offerId=''] - current offer id
     * @return {Boolean} - return true when there is no error and data nodes exist
     * @private
     */
    BSSControllerToolbox.prototype._checkRequestErrorAndData = function (error, data, funName, checkInnerNodes, offerId) {
        if (checkInnerNodes === void 0) { checkInnerNodes = true; }
        if (offerId === void 0) { offerId = ''; }
        var errObj = {
            entityInstanceId: this.getCurrentEntityInstanceId(),
            errorMsg: 'ControllerToolbox ' + funName + ' callback error: ',
            offerId: offerId,
            errorCode: null,
        };
        if (error !== null) {
            if (error.code === 'CALCULATION_ERROR' ||
                error.code === 'VALIDATION_ERROR') {
                errObj.errorMsg = error.msg;
                errObj.errorCode = error.code;
            }
            else {
                errObj.errorMsg +=
                    error.status + ' - ' + error.code + ' - ' + error.msg + '.';
                errObj.errorCode = error.code;
            }
            errObj.status = error.status;
            this._errorHandler(null, errObj);
            return false;
        }
        else if (data === null || typeof data === 'undefined' || data === {}) {
            errObj.errorMsg += 'Configuration data is empty.';
            this._errorHandler(null, errObj);
            return false;
        }
        else if (data[offerId] &&
            typeof data[offerId].error_code !== 'undefined') {
            errObj.errorMsg += 'Configuration data are no good.';
            this._errorHandler(null, errObj);
            return false;
        }
        // check if all mandatory config nodes are defined
        else if (checkInnerNodes) {
            var self_2 = this;
            var dataNodes_1 = {};
            var allDataNodesExists_1 = true;
            if (funName === 'setFlowConfiguration') {
                // check nodes: data.flowId, data.initialEntityId, data.commonURLs,
                // data.commonURLs.entitySimulatorURL, data.commonURLs.entityDataURL, data.commonURLs.entityTranslationsURL
                if (data.entities) {
                    dataNodes_1 = {
                        base: ['flowId', 'initialEntityId'],
                        entities: {},
                    };
                    toolbox._.forEach(data.entities, function (value, key) {
                        dataNodes_1.entities[key] = {
                            base: ['commonURLs', 'container'],
                            commonURLs: [
                                'entityLayoutURL',
                                'entityTextsAndFormattersURL',
                                'entityDataURL',
                            ],
                        };
                    });
                }
                else {
                    dataNodes_1 = {
                        base: ['flowId', 'initialEntityId', 'commonURLs'],
                        commonURLs: ['entitySimulatorURL', 'entityDataURL'],
                    };
                }
            }
            else if (funName === 'getCalculatedOffer') {
                // check nodes: data.resultData, data.layout, data.layout.components, data.layout.formatters, data.layout.texts, data.layout.theme
                dataNodes_1 = {
                    base: ['resultData', 'layout'],
                    layout: ['components', 'texts', 'formatters'],
                };
            }
            // else if (checkInnerNodes && (funName === 'textReceived')) {} // there is no special nodes to check
            // check if all nodes from dataNodes exists
            toolbox._.forEach(dataNodes_1, function (nodesArray, nodeParent) {
                if (!allDataNodesExists_1) {
                    return false;
                }
                if (nodeParent === 'base') {
                    toolbox._.forEach(nodesArray, function (nodeName) {
                        if (!self_2._hasConfigNode(data[nodeName], nodeName)) {
                            allDataNodesExists_1 = false;
                            return false;
                        }
                    });
                }
                else if (nodeParent === 'entities') {
                    toolbox._.forEach(nodesArray, function (values, key) {
                        // check all entities in response.
                        toolbox._.forEach(nodesArray[key], function (nodesArray, nodeParent) {
                            // value
                            if (nodeParent === 'base') {
                                toolbox._.forEach(nodesArray, function (nodeName) {
                                    if (!self_2._hasConfigNode(data.entities[key][nodeName], nodeName)) {
                                        allDataNodesExists_1 = false;
                                        return false;
                                    }
                                });
                            }
                            else {
                                toolbox._.forEach(nodesArray, function (nodeName) {
                                    // value
                                    if (!self_2._hasConfigNode(data.entities[key][nodeParent][nodeName], nodeName)) {
                                        allDataNodesExists_1 = false;
                                        return false;
                                    }
                                });
                            }
                        });
                    });
                }
                else {
                    toolbox._.forEach(nodesArray, function (nodeName) {
                        // value
                        if (!self_2._hasConfigNode(data[nodeParent][nodeName], nodeName)) {
                            allDataNodesExists_1 = false;
                            return false;
                        }
                    });
                }
            });
            // if some nodes don't exist or are empty, show error
            if (!allDataNodesExists_1) {
                errObj.errorMsg =
                    'ControllerToolbox function:' +
                        funName +
                        ' request verification fail. Not all mandatory request field are in response';
                this._errorHandler(null, errObj);
                return false;
            }
        }
        // there is no error and all mandatory nodes exists
        // merge the latest data and validation error list if it's occurred
        if (data.error_code === 'VALIDATOR_ERROR') {
            var currentProdId = this._flowEntities[this._currentEntityId].currentProdId;
            if (Object.hasOwn(data, 'resultData')) {
                data.resultData[currentProdId]['errors'] = data.errors;
            }
            else if (Object.hasOwn(data, currentProdId)) {
                data[currentProdId]['errors'] = data.errors;
            }
            delete data.errors;
        }
        return true;
    };
    BSSControllerToolbox.prototype._checkNode = function (arrayElement, nodeName) {
        if (!self._hasConfigNode(arrayElement, nodeName)) {
            return false;
        }
    };
    /**
     * @desc method prepares quoteId and other params received from backend
     * @param {Object} offer
     * @private
     */
    BSSControllerToolbox.prototype._setFinanceObject = function (offer) {
        var _this = this;
        this._financeObject = {};
        var parameters = [
            'quoteId',
            'monthlyPayment',
            'downpayment',
            'firstInstalmentAmount',
            'duration',
            'apr',
            'residualValue',
            'productCode',
            'disclaimer',
            'accessories',
            'costs',
            'services',
            'downpaymentPercentage',
            'balloonRate',
            'balloon',
            'effectiveInterestRate',
            'totalCostOfCredit',
            'acceptanceFee',
            'administrationFee',
        ];
        parameters.forEach(function (item) {
            if (toolbox._.has(offer, item)) {
                switch (item) {
                    case 'quoteId':
                        _this._financeObject.financeQuoteId = offer.quoteId;
                        break;
                    case 'downpayment':
                        _this._financeObject.downpayment = offer.downpayment;
                        break;
                    case 'firstInstalmentAmount':
                        _this._financeObject.downpayment = offer.firstInstalmentAmount;
                        break;
                    case 'residualValue':
                        _this._financeObject.guaranteedFutureValue = offer.residualValue;
                        break;
                    case 'disclaimer':
                        _this._financeObject.legalText = offer.disclaimer;
                        break;
                    default:
                        _this._financeObject[item] = offer[item];
                }
            }
        });
    };
    /**
     * @desc error handling - show error massage or revert entity state to previous state
     * @param {String} publisherName
     * @param {Object} errorObject - error object with entityInstanceId and errorMsg
     * @param {Boolean} [isComponentError=false] - set to true when component error occurs during an update
     * @emits {bss-hide-entity-overlay-$ENTITY-INSTANCE-ID} when entity state is reverted
     * @emits {bss-received-request-$ENTITY-INSTANCE-ID} when received request
     * @private
     */
    BSSControllerToolbox.prototype._errorHandler = function (publisherName, errorObject, isComponentError) {
        if (isComponentError === void 0) { isComponentError = false; }
        var configGeneralTexts;
        var generalRevertStateMsg;
        var entityInstanceId;
        try {
            configGeneralTexts = Object.assign({}, this._currentData.layout.texts.general);
            generalRevertStateMsg = configGeneralTexts.revertStateMsg;
            // if an error occurs CALCULATION_ERROR and display the specified message
            if (errorObject.errorCode === 'CALCULATION_ERROR' ||
                (errorObject.errorCode === 'VALIDATION_ERROR' &&
                    errorObject.status === 400)) {
                configGeneralTexts.revertStateMsg = errorObject.errorMsg;
            }
            if (errorObject.errorCode === 'OA_TIMEOUT_ERROR') {
                configGeneralTexts.revertStateTitle =
                    configGeneralTexts.oaTimeoutErrorTitle;
                configGeneralTexts.revertStateMsg =
                    configGeneralTexts.oaTimeoutErrorMessage;
            }
            if (errorObject.errorCode === 'DEFAULT_ERROR') {
                configGeneralTexts.revertStateTitle = configGeneralTexts.errorInfoTitle;
                configGeneralTexts.revertStateMsg = configGeneralTexts.errorInfoMsg;
            }
            if (errorObject.errorCode === 'ADDRESS_NOT_FOUND') {
                configGeneralTexts.revertStateTitle =
                    configGeneralTexts.address_lookup_too_many_entries_error_title;
                configGeneralTexts.revertStateMsg =
                    configGeneralTexts.address_lookup_too_many_entries_error;
            }
        }
        catch (err) {
            configGeneralTexts = {
                errorInfoTitle: 'Content failed to load',
                errorInfoMsg: 'We experienced a technical issue. Our team is working on getting this fixed as soon as we can. Please try again later.',
                revertStateTitle: 'VALUES ERROR',
                revertStateMsg: 'Values you selected are no good because of reasons. We reverted them back.',
            };
        }
        if (window.PreLoader && window.PreLoader.running) {
            // On error remove PreLoader.
            window.PreLoader.stop();
        }
        // if an error occurs before entity init, show error template
        if (typeof errorObject.entityInstanceId === 'undefined') {
            var $simulatorErrorTemplate = toolbox.$(simulatorTemplates.errorTemplate(configGeneralTexts));
            var $currentEntityContainer = this._entityContainer;
            if (typeof this._flowEntities[this._currentEntityId] !== 'undefined') {
                $currentEntityContainer =
                    this._flowEntities[this._currentEntityId].entityContainer;
            }
            $currentEntityContainer.html($simulatorErrorTemplate);
            $currentEntityContainer.removeClass(HIDDEN_CLASS).show();
            this._simulatorErrorTemplateLoaded = true;
            this._eventManager.publish('bss-hide-entity-overlay-' + this.getCurrentEntityInstanceId(), UC_PUBLISHER_NAME, {});
        }
        // else revert its state to previous one (entity is already created)
        else {
            this._eventManager.publish('bss-entity-error-' + errorObject.entityInstanceId, UC_PUBLISHER_NAME, {});
            entityInstanceId = this.getCurrentEntityInstanceId();
            this._revertEntityOffer(configGeneralTexts, errorObject, isComponentError);
            this._eventManager.publish('bss-hide-entity-overlay-' + entityInstanceId, UC_PUBLISHER_NAME, Object.assign({}, this.getCustomOffer(), {
                entityRevertedOnError: true,
            }));
        }
        configGeneralTexts.revertStateMsg = generalRevertStateMsg;
        entityInstanceId = this.getCurrentEntityInstanceId();
        this._eventManager.publish('bss-received-request-' + entityInstanceId, UC_PUBLISHER_NAME, {});
    };
    /**
     * @desc error handling - revert offer to previous state if error occurs after entity initialization
     * @param {Object} configGeneralTexts - general texts object
     * @param {Object} errorObject - error object
     * @param {Boolean} isComponentError - set to true when component error occurs during an update
     * @emits {bss-entity-revert-state-$ENTITY-INSTANCE-ID} - published ony when isComponentError is true
     * @emits {bss-data-received-$ENTITY-INSTANCE-ID}
     * @private
     */
    BSSControllerToolbox.prototype._revertEntityOffer = function (configGeneralTexts, errorObject, isComponentError) {
        var isAmd = !!this._entityContainer.hasClass('bss-amd');
        var entityHeight = toolbox
            .$(this._currentEntity._entitySelector)
            .outerHeight();
        var revertTemplateHeight = toolbox
            .$(this._simulatorRevertTemplate)
            .outerHeight();
        var prevOffer = this._flowEntities[this._currentEntityId].customOfferPrevious;
        var defOffer = this.getDefaultOffer();
        var offer = toolbox.$.isEmptyObject(prevOffer) ? defOffer : prevOffer;
        var singleProdResultData = JSON.parse(JSON.stringify(offer));
        // set previous offer as current one
        this._flowEntities[this._currentEntityId].customOffer = offer;
        if (isComponentError) {
            this._eventManager.publish('bss-entity-revert-state-' + errorObject.entityInstanceId, UC_PUBLISHER_NAME, {});
        }
        else {
            this._lastUpdateTimestamp = null;
        }
        toolbox._.each(singleProdResultData.resultData, function (val, key) {
            if (key !== errorObject.offerId) {
                delete singleProdResultData.resultData[key];
            }
        });
        singleProdResultData.updateTimestamp = this._lastUpdateTimestamp;
        // some error from Entity _onDataRecived doesn't return errorObject.offerId
        if (Object.keys(singleProdResultData.resultData).length === 0) {
            singleProdResultData = JSON.parse(JSON.stringify(this.getCustomOffer()));
        }
        if (errorObject.errorCode !== 'ADDRESS_NOT_FOUND') {
            this._eventManager.publish('bss-data-received-' + errorObject.entityInstanceId, UC_PUBLISHER_NAME, singleProdResultData);
        }
        // show snackbar with information about the error and state revert - from config.texts.general
        var $simulatorRevertTemplate = toolbox.$(simulatorTemplates.revertTemplate(configGeneralTexts));
        var $currentEntityContainer = this._flowEntities[this._currentEntityId].entityContainer;
        $currentEntityContainer.append($simulatorRevertTemplate);
        if (!isAmd) {
            var componentPositionTop = typeof errorObject.componentId !== 'undefined'
                ? toolbox.$('#' + errorObject.componentId).offset().top
                : 0;
            var entityMiddlePos = entityHeight / 2 - revertTemplateHeight / 2;
            var top_2 = typeof errorObject.componentId !== 'undefined'
                ? componentPositionTop
                : entityMiddlePos;
            $simulatorRevertTemplate.css({ top: top_2 + 'px' }).fadeIn(1000);
        }
        setTimeout(function () {
            $simulatorRevertTemplate.fadeOut(400, function () {
                $currentEntityContainer.find('.bss-entity-revert-state-info').remove();
            });
        }, 5000);
        var $closeButton = $simulatorRevertTemplate.find('.bss-entity-revert-state-info-close-icon');
        $closeButton.on('click', function () {
            return $currentEntityContainer.find('.bss-entity-revert-state-info').remove();
        });
    };
    /**
     * @desc gets the flow configuration and sets it localLy
     * @public
     */
    BSSControllerToolbox.prototype.getFlowConfiguration = function (parameters) {
        if (parameters === void 0) { parameters = null; }
        if (this._environment === null) {
            this._environment = 'PRODUCTION';
        }
        this._environment = this._environment.toUpperCase();
        var entityInstanceId = this.getCurrentEntityInstanceId();
        // Should be called when this._flowId is ready
        this._flowConfigUrl = this._getMockUrl() || this._flowConfigUrl;
        if (parameters === null) {
            parameters = {
                brand: this._flowProps.brand,
                country: this._country,
                environment: this._environment,
                language: this._flowProps.lang,
            };
        }
        else if (Object.hasOwn(parameters, 'elementType')) {
            parameters = {
                brand: this._flowProps.brand,
                elementType: parameters.elementType,
                language: this._flowProps.lang,
                country: parameters.country,
                environment: parameters.environment
                    ? parameters.environment.toUpperCase()
                    : this._environment,
            };
        }
        this._BSSBackendConnector.getConfiguration(this._flowConfigUrl, this.setFlowConfiguration.bind(this), JSON.stringify(parameters), entityInstanceId);
    };
    /**
     * @desc publish tagging events for loaded and closing simulator
     * @param {String} entityId
     * @param {String} eventName
     * @private
     */
    BSSControllerToolbox.prototype._publishTaggingEvent = function (entityId, eventName) {
        if (this._simulatorErrorTemplateLoaded !== true) {
            var eventObjParams = {
                throwEvent: eventName,
                productId: this._flowEntities[entityId].currentProdId,
                entityId: entityId,
            };
            this._eventManager.publish(eventName, UC_PUBLISHER_NAME, eventObjParams);
        }
    };
    /**
     * @desc destroy entity with given id
     * @param {Number} entityId
     * @public
     */
    BSSControllerToolbox.prototype.destroyEntity = function (entityId) {
        var _a, _b, _c, _d, _e;
        if (this.entityBuilder !== null) {
            if (this.entityBuilder._entityMap.size > 0 &&
                this._flowParams.country === 'ru') {
                this._publishTaggingEvent(entityId, 'simulatorConfirmClosing_' +
                    this.entityBuilder._entityMap.entries().next().value[1]
                        ._entityActiveProductId);
                (_b = (_a = toolbox.dxpTagging) === null || _a === void 0 ? void 0 : _a[entityId]) === null || _b === void 0 ? void 0 : _b.publish('saveEntity', toolbox.resultDataFlat[toolbox.currentProductId]);
            }
            else if (this.entityBuilder._entityMap.size > 0 &&
                !this._isCloseButton) {
                this._publishTaggingEvent(entityId, 'simulatorClosing_' +
                    this.entityBuilder._entityMap.entries().next().value[1]
                        ._entityActiveProductId);
                (_d = (_c = toolbox.dxpTagging) === null || _c === void 0 ? void 0 : _c[entityId]) === null || _d === void 0 ? void 0 : _d.publish('closeEntity', {
                    entityId: entityId,
                    productId: toolbox.currentProductId,
                    integrationMode: ((_e = this._options) === null || _e === void 0 ? void 0 : _e.integrationMode) || null,
                });
            }
            this.entityBuilder.destroyEntity(entityId);
            this._entityCounter--;
        }
        toolbox.$(this._entitySelector).children().remove();
        toolbox.$(this._entitySelector).removeAttr('style').removeClass();
        this._eventManager.unsubscribeByOwner('bss-create-flow-' +
            this._flowId +
            '-entity-' +
            this._getEntityOrder(entityId));
    };
    /**
     * Escape string, preventing for Cross Site Scripting.
     * @param value
     * @returns {String}
     */
    BSSControllerToolbox.prototype.ecs = function (value) {
        return value.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };
    return BSSControllerToolbox;
}(BaseControllerToolbox));
export { BSSControllerToolbox };
